import type { StitchImageKey } from "../config/stitch-images";
import type { Product } from "./types";

/** Claves de imagen para la ficha: principal primero, luego adicionales, sin duplicar. */
export function productGalleryImageKeys(product: Product): readonly StitchImageKey[] {
  const extra = product.additionalImageKeys ?? [];
  const seen = new Set<StitchImageKey>();
  const out: StitchImageKey[] = [];
  for (const k of [product.imageKey, ...extra]) {
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

export const products: readonly Product[] = [
  {
    id: "p-taza-boticario",
    slug: "taza-arcana-ceramica-11",
    sku: "HK-TAZ-ARC-C11",
    name: "Taza Diarios de la boticaria — cerámica 11 oz",
    priceCop: 52000,
    collectionId: "arcana-boticario",
    category: "mug",
    imageKey: "catalogMug",
    additionalImageKeys: ["colTemple", "craftDetail"],
    oneLiner: "Cerámica para café o té, con la calma de la serie de la boticaria.",
    shortDescription:
      "Taza de cerámica con sublimación nítida. Inspirada en Los diarios de la boticaria. Máximo 10 tazas con este diseño.",
    editionNote: "Máximo 10 tazas con este diseño",
    specs: {
      material: "Cerámica",
      capacityOz: "11",
      dishwasherSafe: true,
    },
  },
  {
    id: "p-taza-sello-vidrio",
    slug: "taza-sello-vidrio-10",
    sku: "HK-TAZ-SEL-V10",
    name: "Taza Jujutsu — vidrio 10 oz",
    priceCop: 58000,
    collectionId: "sello-maldito",
    category: "mug",
    imageKey: "productKatanaMug",
    additionalImageKeys: ["shogunHero", "catalogMug"],
    oneLiner: "Vidrio transparente; se ve genial con fondos oscuros.",
    shortDescription:
      "Taza de vidrio borosilicato. Línea inspirada en Jujutsu Kaisen. Máximo 10 unidades de este modelo.",
    editionNote: "Máximo 10 tazas con este diseño",
    specs: {
      material: "Vidrio",
      capacityOz: "10",
      dishwasherSafe: false,
    },
  },
  {
    id: "p-taza-neon-peltre",
    slug: "taza-neon-peltre-12",
    sku: "HK-TAZ-NEO-P12",
    name: "Taza Kaiju distrito — peltre 12 oz",
    priceCop: 48000,
    collectionId: "neon-distrito",
    category: "mug",
    imageKey: "catalogMug",
    additionalImageKeys: ["colCyberpunkStreet", "catalogFlask"],
    oneLiner: "Peltre liviano, estilo clásico de campo urbano.",
    shortDescription:
      "Taza de peltre mate, cómoda para el escritorio. Inspirada en Kaiju No. 8 y la ciudad de noche. Máximo 10 unidades.",
    editionNote: "Máximo 10 tazas con este diseño",
    specs: {
      material: "Peltre",
      capacityOz: "12",
      dishwasherSafe: false,
    },
  },
  {
    id: "p-mousepad-piloto",
    slug: "mousepad-bitacora-xl",
    sku: "HK-MP-BIT-XL",
    name: "Mouse pad Dr. Stone — XL",
    priceCop: 89000,
    collectionId: "bitacora-piloto",
    category: "mousepad",
    imageKey: "catalogJournal",
    additionalImageKeys: ["catalogCyberShirt", "craftDetail"],
    oneLiner: "Mucho espacio para jugar o estudiar con calma.",
    shortDescription:
      "Mouse pad de tela con base antideslizante, tamaño XL. Línea Bitácora del científico (Dr. Stone).",
    specs: {
      widthCm: 90,
      heightCm: 40,
      thicknessMm: 3,
      stitchedEdge: true,
      baseMaterial: "Goma natural antideslizante",
    },
  },
  {
    id: "p-posavasos-santuario",
    slug: "portavasos-santuario-set4",
    sku: "HK-PV-SAN-04",
    name: "Portavasos bosque y espíritus — set ×4",
    priceCop: 36000,
    collectionId: "santuario-silencio",
    category: "coaster",
    imageKey: "catalogKeychain",
    additionalImageKeys: ["craftDetail", "colTemple"],
    oneLiner: "Cuatro piezas para la mesa, con aire de naturaleza y calma.",
    shortDescription:
      "Set de cuatro portavasos con sublimación y base de corcho. Inspiración en Princesa Mononoke y afines.",
    specs: {
      material: "MDF + corcho",
      setQuantity: 4,
      diameterCm: 9,
    },
  },
  {
    id: "p-termo-mar",
    slug: "termo-ruta-500",
    sku: "HK-TR-MAR-500",
    name: "Termo One Piece — 500 ml",
    priceCop: 125000,
    collectionId: "ruta-mar-errante",
    category: "thermos",
    imageKey: "catalogFlask",
    additionalImageKeys: ["opOcean", "opAnchorMug"],
    oneLiner: "Acero inoxidable para el día largo o el paseo.",
    shortDescription:
      "Termo de doble pared que mantiene la temperatura. Línea Ruta del mar errante (One Piece).",
    specs: {
      capacityMl: 500,
      bodyMaterial: "Acero inoxidable 304",
      keepsHotHours: 8,
    },
  },
  {
    id: "p-poster-luna",
    slug: "poster-alu-luna-a3",
    sku: "HK-PO-LUN-A3",
    name: "Poster aluminio Sailor — A3",
    priceCop: 145000,
    collectionId: "luz-guardian-lunar",
    category: "poster_aluminum",
    imageKey: "catalogPoster",
    additionalImageKeys: ["productMetalPrint", "colNeonHelmet"],
    oneLiner: "Metal brillante que se ve bien a la luz.",
    shortDescription:
      "Chapa de aluminio sublimada, tamaño A3. Inspirada en Sailor Moon y la línea Luz de guardián lunar.",
    specs: {
      widthCm: 29.7,
      heightCm: 42,
      finish: "Metalizado",
    },
  },
  {
    id: "p-puzzle-bosque",
    slug: "rompecabezas-santuario-500",
    sku: "HK-RP-SAN-500",
    name: "Rompecabezas bosque — 500 piezas",
    priceCop: 98000,
    collectionId: "santuario-silencio",
    category: "puzzle",
    imageKey: "productMetalPrint",
    additionalImageKeys: ["craftDetail", "colTemple"],
    oneLiner: "Para armar con calma, pieza a pieza.",
    shortDescription:
      "Rompecabezas de cartón con acabado satinado. Misma línea de naturaleza y espíritus que los portavasos.",
    specs: {
      pieceCount: 500,
      finishedSizeCm: "48 × 36 cm",
      material: "Cartón blueback 2 mm",
    },
  },
  {
    id: "p-llavero-eclipsio",
    slug: "llavero-eclipsio-acrilico",
    sku: "HK-LL-ECL-01",
    name: "Llavero Eclipsio — acrílico",
    priceCop: 22000,
    collectionId: "caja-eclipsio",
    category: "keychain",
    imageKey: "catalogKeychain",
    additionalImageKeys: ["catalogJournal", "catalogMug"],
    oneLiner: "Doble cara, liviano y resistente.",
    shortDescription:
      "Llavero acrílico con arte entre capas. Combina con la caja sorpresa Eclipsio y otras líneas.",
    specs: {
      material: "Acrílico",
      doubleSided: true,
      hardware: "Argolla metálica",
    },
  },
  {
    id: "p-camiseta-shogun",
    slug: "camiseta-shogun-tech",
    sku: "HK-TS-SHO-M",
    name: "Camiseta Jujutsu — oversize",
    priceCop: 135000,
    collectionId: "sello-maldito",
    category: "tee",
    imageKey: "shogunHero",
    additionalImageKeys: ["shogunThumbFabric", "shogunThumbBack", "shogunThumbSide"],
    oneLiner: "Corte amplio, buena caída.",
    shortDescription:
      "Camiseta de algodón peinado, oversize. Estampa según diseño (sublimación o DTF). Máximo 10 camisetas con este estampado.",
    editionNote: "Talla M en ficha; consultar otras tallas por WhatsApp",
    specs: {
      fabric: "Algodón peinado 180 g/m²",
      fit: "Oversize",
      availableSizes: ["S", "M", "L", "XL"],
      printType: "Sublimación / DTF (según arte)",
    },
  },
  {
    id: "p-buzo-neon",
    slug: "buzo-neon-ronin",
    sku: "HK-BZ-NEO-L",
    name: "Buzo Kaiju distrito",
    priceCop: 198000,
    collectionId: "neon-distrito",
    category: "hoodie",
    imageKey: "relatedVoidHoodie",
    additionalImageKeys: ["relatedNeonWindbreaker", "catalogCyberShirt"],
    oneLiner: "Abrigo cómodo para salir de noche o quedarte en casa.",
    shortDescription:
      "Buzo en mezcla algodón/poliéster con bolsillo canguro. Misma línea urbana que las tazas Kaiju distrito.",
    specs: {
      fabric: "French terry 280 g/m²",
      fit: "Oversize",
      availableSizes: ["S", "M", "L", "XL"],
      printType: "Estampa central + detalle manga",
      hasPouch: true,
    },
  },
  {
    id: "p-mystery-premium",
    slug: "caja-eclipsio-premium",
    sku: "HK-MY-ECL-PR",
    name: "Caja sorpresa Eclipsio — Premium",
    priceCop: 320000,
    collectionId: "caja-eclipsio",
    category: "mystery_box",
    imageKey: "catalogJournal",
    additionalImageKeys: ["catalogKeychain", "productMetalPrint"],
    oneLiner: "Varias piezas adentro y algo que no vendemos suelto.",
    shortDescription:
      "Caja con productos de nuestras líneas y un detalle exclusivo. El contenido varía; pregunta por el tema disponible.",
    editionNote: "Contenido variable; consultar tema disponible",
    specs: {
      tier: "Premium",
      minItems: 4,
      maxRetailValueCop: 420000,
      includesExclusivePrint: true,
    },
  },
];

export function getProductBySlug(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}

export function getProductsByCollection(collectionSlug: string): readonly Product[] {
  return products.filter((p) => p.collectionId === collectionSlug);
}

export function getProductsByCategory(category: Product["category"]): readonly Product[] {
  return products.filter((p) => p.category === category);
}
